import { Suspense } from 'react';
import { User } from 'firebase/auth';
import { doc } from 'firebase/firestore';
import { useAuth, useFirestore, useFirestoreDocDataOnce } from 'reactfire';

import { Divider, IconButton, ListItem, Skeleton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import {
  SidebarRoot,
  TopBar,
  Navigation,
  SidebarContentWrapper,
} from '../../../styled/Sidebar.styled';
import {
  AppBar,
  UsersList,
  ConversationsList,
  LicensingTerms,
} from '../../index';

import { logout } from '../../../utils/api/auth';

interface SidebarProps {
  open: boolean;
  onOpen: (open: boolean) => void;
}

const SidebarList = ({ currentUser }: { currentUser: User }) => {
  const firestore = useFirestore();
  const { data: userMeta } = useFirestoreDocDataOnce(
    doc(firestore, 'users', currentUser.uid),
  );

  const showUsersList =
    userMeta.role === 'admin' || userMeta.role === 'auditor';

  return (
    <>
      {showUsersList ? (
        <UsersList />
      ) : (
        <ConversationsList userId={currentUser.uid} />
      )}
    </>
  );
};

const Sidebar = ({ open, onOpen }: SidebarProps) => {
  const auth = useAuth();

  const handleSidebarOpen = () => {
    onOpen(true);
  };

  const handleSidebarClose = () => {
    onOpen(false);
  };

  const handleSignOut = () => {
    logout(auth).then(() => window.location.replace('/'));
  };

  return (
    <SidebarRoot>
      <AppBar
        sidebarOpen={open}
        currentUser={auth.currentUser}
        onSidebarOpen={handleSidebarOpen}
        onSignOut={handleSignOut}
      />
      <Navigation open={open} variant="persistent" anchor="left">
        <TopBar>
          <IconButton onClick={handleSidebarClose}>
            <ChevronLeftIcon />
          </IconButton>
        </TopBar>
        <Divider />
        <SidebarContentWrapper>
          <Suspense
            fallback={
              <ListItem>
                <Skeleton variant="text" width="100%" />
              </ListItem>
            }
          >
            {auth.currentUser && <SidebarList currentUser={auth.currentUser} />}
          </Suspense>
          <LicensingTerms />
        </SidebarContentWrapper>
      </Navigation>
    </SidebarRoot>
  );
};

export default Sidebar;
